import { CAPABILITY_SCOPES, RELATIONSHIP_ROLES, getCapabilityDefinition, isCapabilityKey, } from '../../compass-capability-registry/dist/index.js';
/**
 * Structure/meaning of the persisted format, independent of revision number.
 *   1  Position grants only (every policy stored before relationship-role grants existed; byte-for-byte unchanged).
 *   2  Position grants plus a non-empty `relationshipRoleGrants` section.
 * The version is DERIVED from content (policySchemaVersionFor), so a policy that does not use role grants keeps version 1
 * and an unchanged canonical form and fingerprint; code that predates version 2 rejects a version-2 revision (fail closed).
 */
export const POSITION_POLICY_SCHEMA_VERSION = 1;
export const POSITION_POLICY_ROLE_GRANTS_SCHEMA_VERSION = 2;
export const POSITION_ORGANIZATION_LEVELS = ['district', 'council'];
export const POSITION_POLICY_SCOPES = ['self', 'subcommittee', 'district', 'council'];
export const POSITION_POLICY_STATES = ['draft', 'published'];
export const POSITION_POLICY_OWNER_KINDS = ['compassDefault', 'council'];
export const POSITION_POLICY_GRANT_STATES = ['granted'];
export const PROTECTED_BASELINE_STATUSES = ['candidate', 'approved'];
export const POLICY_VALIDATION_ERROR_CODES = [
    'INVALID_POLICY_SHAPE',
    'UNSUPPORTED_POLICY_FIELD',
    'INVALID_POSITION_ID',
    'DUPLICATE_POSITION',
    'UNKNOWN_POSITION',
    'INVALID_GRANT_STATE',
    'UNKNOWN_CAPABILITY',
    'CAPABILITY_NOT_POSITION_ASSIGNABLE',
    'CAPABILITY_RESERVED',
    'UNKNOWN_RELATIONSHIP_ROLE',
    'DUPLICATE_RELATIONSHIP_ROLE',
    'CAPABILITY_NOT_ROLE_GRANTABLE',
    'INVALID_SCOPE',
    'SCOPE_NOT_SUPPORTED',
    'SCOPE_EXCEEDS_POSITION_LEVEL',
    'DUPLICATE_GRANT',
    'MISSING_COMPANION_CAPABILITY',
    'PROTECTED_BASELINE_MISSING',
    'INVALID_SCHEMA_VERSION',
    'INVALID_REVISION_STATE',
    'INVALID_REVISION_METADATA',
    'INVALID_FINGERPRINT',
];
const SNAPSHOT_FIELDS = new Set(['entries', 'relationshipRoleGrants']);
const ROLE_ENTRY_FIELDS = new Set(['role', 'grants']);
const ROLE_GRANT_FIELDS = new Set(['capabilityKey', 'state']);
const ENTRY_FIELDS = new Set(['positionId', 'grants']);
const GRANT_FIELDS = new Set(['capabilityKey', 'scopeCeiling', 'state']);
const REVISION_FIELDS = new Set([
    'revisionId', 'revisionNumber', 'schemaVersion', 'owner', 'state',
    'createdAt', 'createdByUid', 'previousRevisionId', 'reason', 'policy',
    'fingerprint', 'publishedAt', 'publishedByUid',
]);
const OWNER_FIELDS = new Set(['kind', 'ownerId']);
const SCOPE_RANK = { self: 0, subcommittee: 1, district: 2, council: 3 };
function isRecord(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}
function unexpectedFields(value, allowed, path, errors) {
    for (const field of Object.keys(value)) {
        if (!allowed.has(field)) {
            errors.push({
                code: 'UNSUPPORTED_POLICY_FIELD',
                path: `${path}.${field}`,
                message: `Position policy does not support field "${field}".`,
            });
        }
    }
}
export function isStablePositionId(value) {
    return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9_-]{2,127}$/.test(value);
}
function isIsoTimestamp(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(value))
        return false;
    return Number.isFinite(Date.parse(value));
}
function grantSortKey(grant) {
    return `${grant.capabilityKey}\u0000${String(SCOPE_RANK[grant.scopeCeiling]).padStart(2, '0')}\u0000${grant.state}`;
}
function compareStable(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
const ROLE_ORDER = new Map(RELATIONSHIP_ROLES.map((role, index) => [role, index]));
export function hasRelationshipRoleGrants(policy) {
    return (policy.relationshipRoleGrants ?? []).some((entry) => entry.grants.length > 0);
}
/** The schema version a policy must declare: 2 only when it carries role grants, otherwise 1 (existing policies unchanged). */
export function policySchemaVersionFor(policy) {
    return hasRelationshipRoleGrants(policy) ? POSITION_POLICY_ROLE_GRANTS_SCHEMA_VERSION : POSITION_POLICY_SCHEMA_VERSION;
}
/**
 * Canonical form. A policy without role grants canonicalizes to exactly `{ entries }` as it always has, so its serialization
 * and fingerprint are unchanged; an empty role-grants section is treated as absent so one meaning has one fingerprint.
 */
export function canonicalizePositionPolicy(policy) {
    const entries = policy.entries
        .map((entry) => Object.freeze({
        positionId: entry.positionId,
        grants: Object.freeze(entry.grants
            .map((grant) => Object.freeze({
            capabilityKey: grant.capabilityKey,
            scopeCeiling: grant.scopeCeiling,
            state: grant.state,
        }))
            .sort((a, b) => compareStable(grantSortKey(a), grantSortKey(b)))),
    }))
        .sort((a, b) => compareStable(a.positionId, b.positionId));
    const roles = (policy.relationshipRoleGrants ?? [])
        .filter((entry) => entry.grants.length > 0)
        .map((entry) => Object.freeze({
        role: entry.role,
        grants: Object.freeze(entry.grants
            .map((grant) => Object.freeze({ capabilityKey: grant.capabilityKey, state: grant.state }))
            .sort((a, b) => compareStable(a.capabilityKey, b.capabilityKey))),
    }))
        .sort((a, b) => (ROLE_ORDER.get(a.role) ?? 99) - (ROLE_ORDER.get(b.role) ?? 99));
    return roles.length
        ? Object.freeze({ entries: Object.freeze(entries), relationshipRoleGrants: Object.freeze(roles) })
        : Object.freeze({ entries: Object.freeze(entries) });
}
export function serializeCanonicalPolicy(policy) {
    return JSON.stringify(canonicalizePositionPolicy(policy));
}
/** Deterministic semantic identifier, not a cryptographic signature. */
export function fingerprintPositionPolicy(policy) {
    const source = serializeCanonicalPolicy(policy);
    let hash = 0xcbf29ce484222325n;
    for (const byte of new TextEncoder().encode(source)) {
        hash ^= BigInt(byte);
        hash = BigInt.asUintN(64, hash * 0x100000001b3n);
    }
    return `fnv1a64:${hash.toString(16).padStart(16, '0')}`;
}
function scopeAllowedForPosition(scope, level) {
    return level === 'council' || scope !== 'council';
}
function validateGrant(raw, path, position, errors) {
    if (!isRecord(raw)) {
        errors.push({ code: 'INVALID_POLICY_SHAPE', path, message: 'Grant must be an object.', positionId: position.positionId });
        return null;
    }
    unexpectedFields(raw, GRANT_FIELDS, path, errors);
    const capabilityValue = raw.capabilityKey;
    const scopeValue = raw.scopeCeiling;
    const stateValue = raw.state;
    if (stateValue !== 'granted') {
        errors.push({
            code: 'INVALID_GRANT_STATE', path: `${path}.state`, message: 'The only supported grant state is "granted".',
            positionId: position.positionId,
        });
    }
    if (!isCapabilityKey(capabilityValue)) {
        errors.push({
            code: 'UNKNOWN_CAPABILITY', path: `${path}.capabilityKey`, message: 'Capability key is not in the canonical registry.',
            positionId: position.positionId, capabilityKey: typeof capabilityValue === 'string' ? capabilityValue : undefined,
        });
    }
    if (typeof scopeValue !== 'string' || !CAPABILITY_SCOPES.includes(scopeValue)) {
        errors.push({
            code: 'INVALID_SCOPE', path: `${path}.scopeCeiling`, message: 'Scope ceiling is not a canonical scope.',
            positionId: position.positionId, capabilityKey: typeof capabilityValue === 'string' ? capabilityValue : undefined,
        });
    }
    else if (!POSITION_POLICY_SCOPES.includes(scopeValue)) {
        errors.push({
            code: 'INVALID_SCOPE', path: `${path}.scopeCeiling`, message: 'Account and system scopes are not operational Position scopes.',
            positionId: position.positionId, capabilityKey: typeof capabilityValue === 'string' ? capabilityValue : undefined,
        });
    }
    if (!isCapabilityKey(capabilityValue) || !POSITION_POLICY_SCOPES.includes(scopeValue) || stateValue !== 'granted') {
        return null;
    }
    const capability = getCapabilityDefinition(capabilityValue);
    if (!capability.assignableToPositions || capability.authority !== 'operational') {
        errors.push({
            code: 'CAPABILITY_NOT_POSITION_ASSIGNABLE', path: `${path}.capabilityKey`,
            message: 'Account/system authority cannot be assigned through operational Position policy.',
            positionId: position.positionId, capabilityKey: capabilityValue,
        });
    }
    if (capability.status === 'reserved') {
        errors.push({
            code: 'CAPABILITY_RESERVED', path: `${path}.capabilityKey`,
            message: 'This capability is defined but not yet active, so no policy may grant it.',
            positionId: position.positionId, capabilityKey: capabilityValue,
        });
    }
    const scope = scopeValue;
    if (!capability.scopes.includes(scope)) {
        errors.push({
            code: 'SCOPE_NOT_SUPPORTED', path: `${path}.scopeCeiling`, message: 'Capability does not support this scope.',
            positionId: position.positionId, capabilityKey: capabilityValue,
        });
    }
    if (!scopeAllowedForPosition(scope, position.organizationLevel)) {
        errors.push({
            code: 'SCOPE_EXCEEDS_POSITION_LEVEL', path: `${path}.scopeCeiling`,
            message: 'A district Position cannot confer council scope.', positionId: position.positionId, capabilityKey: capabilityValue,
        });
    }
    return { capabilityKey: capabilityValue, scopeCeiling: scope, state: 'granted' };
}
/** Validates the optional `relationshipRoleGrants` section. Every rule here is what keeps financial control out of reach of an assignment. */
function validateRelationshipRoleGrants(raw, errors) {
    const out = [];
    if (!Array.isArray(raw)) {
        errors.push({ code: 'INVALID_POLICY_SHAPE', path: 'policy.relationshipRoleGrants', message: 'Relationship role grants must be an array.' });
        return out;
    }
    const seenRoles = new Set();
    for (const [roleIndex, rawEntry] of raw.entries()) {
        const path = `policy.relationshipRoleGrants[${roleIndex}]`;
        if (!isRecord(rawEntry)) {
            errors.push({ code: 'INVALID_POLICY_SHAPE', path, message: 'Relationship role entry must be an object.' });
            continue;
        }
        unexpectedFields(rawEntry, ROLE_ENTRY_FIELDS, path, errors);
        const role = rawEntry.role;
        if (typeof role !== 'string' || !RELATIONSHIP_ROLES.includes(role)) {
            errors.push({ code: 'UNKNOWN_RELATIONSHIP_ROLE', path: `${path}.role`, message: 'Relationship role is not recognized.', role: typeof role === 'string' ? role : undefined });
            continue;
        }
        if (seenRoles.has(role)) {
            errors.push({ code: 'DUPLICATE_RELATIONSHIP_ROLE', path: `${path}.role`, message: 'Relationship role occurs more than once.', role });
        }
        seenRoles.add(role);
        if (!Array.isArray(rawEntry.grants)) {
            errors.push({ code: 'INVALID_POLICY_SHAPE', path: `${path}.grants`, message: 'Grants must be an array.', role });
            continue;
        }
        const grants = [];
        const seen = new Set();
        for (const [grantIndex, rawGrant] of rawEntry.grants.entries()) {
            const grantPath = `${path}.grants[${grantIndex}]`;
            if (!isRecord(rawGrant)) {
                errors.push({ code: 'INVALID_POLICY_SHAPE', path: grantPath, message: 'Grant must be an object.', role });
                continue;
            }
            unexpectedFields(rawGrant, ROLE_GRANT_FIELDS, grantPath, errors);
            const key = rawGrant.capabilityKey;
            if (rawGrant.state !== 'granted') {
                errors.push({ code: 'INVALID_GRANT_STATE', path: `${grantPath}.state`, message: 'The only supported grant state is "granted".', role });
            }
            if (!isCapabilityKey(key)) {
                errors.push({
                    code: 'UNKNOWN_CAPABILITY', path: `${grantPath}.capabilityKey`, message: 'Capability key is not in the canonical registry.',
                    role, capabilityKey: typeof key === 'string' ? key : undefined,
                });
                continue;
            }
            const capability = getCapabilityDefinition(key);
            if (capability.status === 'reserved') {
                errors.push({ code: 'CAPABILITY_RESERVED', path: `${grantPath}.capabilityKey`, message: 'This capability is defined but not yet active, so no policy may grant it.', role, capabilityKey: key });
            }
            if (!(capability.relationshipRoles ?? []).includes(role)) {
                errors.push({
                    code: 'CAPABILITY_NOT_ROLE_GRANTABLE', path: `${grantPath}.capabilityKey`,
                    message: 'This capability cannot be granted to a relationship role. Financial control, export, print, administration, merging and assignment management must be granted explicitly through a Position.',
                    role, capabilityKey: key,
                });
            }
            if (seen.has(key)) {
                errors.push({ code: 'DUPLICATE_GRANT', path: grantPath, message: 'Capability occurs more than once for this role.', role, capabilityKey: key });
            }
            seen.add(key);
            if (rawGrant.state === 'granted')
                grants.push({ capabilityKey: key, state: 'granted' });
        }
        // Companion rule: only companions the role itself may carry are required (aggregate progress is never a donor-role grant).
        for (const grant of grants) {
            for (const required of getCapabilityDefinition(grant.capabilityKey).requires ?? []) {
                if (!isCapabilityKey(required))
                    continue;
                const roleGrantable = (getCapabilityDefinition(required).relationshipRoles ?? []).includes(role);
                if (roleGrantable && !grants.some((candidate) => candidate.capabilityKey === required)) {
                    errors.push({
                        code: 'MISSING_COMPANION_CAPABILITY', path: `${path}.grants`,
                        message: `${grant.capabilityKey} requires an explicit ${required} grant for the same role.`, role, capabilityKey: required,
                    });
                }
            }
        }
        out.push({ role: role, grants });
    }
    return out;
}
export function validatePositionPolicy(rawPolicy, context) {
    const errors = [];
    const positionById = new Map(context.positions.map((position) => [position.positionId, position]));
    const catalogPositionIds = new Set();
    for (const [index, position] of context.positions.entries()) {
        if (!isStablePositionId(position.positionId)) {
            errors.push({ code: 'INVALID_POSITION_ID', path: `context.positions[${index}].positionId`, message: 'Position ID is not stable.', positionId: position.positionId });
        }
        if (catalogPositionIds.has(position.positionId)) {
            errors.push({ code: 'DUPLICATE_POSITION', path: `context.positions[${index}].positionId`, message: 'Position catalog IDs must be unique.', positionId: position.positionId });
        }
        catalogPositionIds.add(position.positionId);
    }
    for (const [baselineIndex, baseline] of (context.protectedBaselines ?? []).entries()) {
        const seen = new Set();
        for (const [grantIndex, grant] of baseline.grants.entries()) {
            const path = `context.protectedBaselines[${baselineIndex}].grants[${grantIndex}]`;
            if (!isCapabilityKey(grant.capabilityKey)) {
                errors.push({ code: 'UNKNOWN_CAPABILITY', path: `${path}.capabilityKey`, message: 'Baseline capability is unknown.', capabilityKey: String(grant.capabilityKey) });
                continue;
            }
            const definition = getCapabilityDefinition(grant.capabilityKey);
            if (!definition.assignableToPositions || definition.authority !== 'operational') {
                errors.push({ code: 'CAPABILITY_NOT_POSITION_ASSIGNABLE', path: `${path}.capabilityKey`, message: 'Protected baselines cannot contain account/system authority.', capabilityKey: grant.capabilityKey });
            }
            if (!POSITION_POLICY_SCOPES.includes(grant.scopeCeiling)) {
                errors.push({ code: 'INVALID_SCOPE', path: `${path}.scopeCeiling`, message: 'Baseline scope is not operational.', capabilityKey: grant.capabilityKey });
            }
            else if (!definition.scopes.includes(grant.scopeCeiling)) {
                errors.push({ code: 'SCOPE_NOT_SUPPORTED', path: `${path}.scopeCeiling`, message: 'Baseline scope is unsupported by the capability.', capabilityKey: grant.capabilityKey });
            }
            if (seen.has(grant.capabilityKey)) {
                errors.push({ code: 'DUPLICATE_GRANT', path, message: 'Protected baseline capability occurs more than once.', capabilityKey: grant.capabilityKey });
            }
            seen.add(grant.capabilityKey);
        }
    }
    if (!isRecord(rawPolicy)) {
        return { valid: false, errors: [{ code: 'INVALID_POLICY_SHAPE', path: 'policy', message: 'Policy must be an object.' }] };
    }
    unexpectedFields(rawPolicy, SNAPSHOT_FIELDS, 'policy', errors);
    if (!Array.isArray(rawPolicy.entries)) {
        errors.push({ code: 'INVALID_POLICY_SHAPE', path: 'policy.entries', message: 'Policy entries must be an array.' });
        return { valid: false, errors };
    }
    const entries = [];
    const seenPositions = new Set();
    for (const [entryIndex, rawEntry] of rawPolicy.entries.entries()) {
        const path = `policy.entries[${entryIndex}]`;
        if (!isRecord(rawEntry)) {
            errors.push({ code: 'INVALID_POLICY_SHAPE', path, message: 'Position policy entry must be an object.' });
            continue;
        }
        unexpectedFields(rawEntry, ENTRY_FIELDS, path, errors);
        if (!isStablePositionId(rawEntry.positionId)) {
            errors.push({ code: 'INVALID_POSITION_ID', path: `${path}.positionId`, message: 'A stable Position ID is required.' });
            continue;
        }
        const positionId = rawEntry.positionId;
        if (seenPositions.has(positionId)) {
            errors.push({ code: 'DUPLICATE_POSITION', path: `${path}.positionId`, message: 'Position occurs more than once.', positionId });
        }
        seenPositions.add(positionId);
        const position = positionById.get(positionId);
        if (!position) {
            errors.push({ code: 'UNKNOWN_POSITION', path: `${path}.positionId`, message: 'Position ID is not in the supplied catalog.', positionId });
            continue;
        }
        if (!Array.isArray(rawEntry.grants)) {
            errors.push({ code: 'INVALID_POLICY_SHAPE', path: `${path}.grants`, message: 'Grants must be an array.', positionId });
            continue;
        }
        const grants = [];
        const seenCapabilities = new Set();
        for (const [grantIndex, rawGrant] of rawEntry.grants.entries()) {
            const grant = validateGrant(rawGrant, `${path}.grants[${grantIndex}]`, position, errors);
            if (!grant)
                continue;
            if (seenCapabilities.has(grant.capabilityKey)) {
                errors.push({
                    code: 'DUPLICATE_GRANT', path: `${path}.grants[${grantIndex}]`, message: 'Capability occurs more than once for this Position.',
                    positionId, capabilityKey: grant.capabilityKey,
                });
            }
            seenCapabilities.add(grant.capabilityKey);
            grants.push(grant);
        }
        entries.push({ positionId, grants });
    }
    for (const entry of entries) {
        const grants = new Map(entry.grants.map((grant) => [grant.capabilityKey, grant]));
        for (const grant of entry.grants) {
            const definition = getCapabilityDefinition(grant.capabilityKey);
            for (const required of definition.requires ?? []) {
                if (!isCapabilityKey(required))
                    continue;
                const companion = grants.get(required);
                if (!companion || SCOPE_RANK[companion.scopeCeiling] < SCOPE_RANK[grant.scopeCeiling]) {
                    errors.push({
                        code: 'MISSING_COMPANION_CAPABILITY', path: `policy.entries.${entry.positionId}.grants`,
                        message: `${grant.capabilityKey} requires an explicit ${required} grant at the same or broader scope ceiling.`,
                        positionId: entry.positionId, capabilityKey: required,
                    });
                }
            }
        }
    }
    for (const baseline of context.protectedBaselines ?? []) {
        if (baseline.status !== 'approved')
            continue;
        for (const position of context.positions.filter((candidate) => candidate.classIds.includes(baseline.positionClassId))) {
            const entry = entries.find((candidate) => candidate.positionId === position.positionId);
            for (const required of baseline.grants) {
                const actual = entry?.grants.find((grant) => grant.capabilityKey === required.capabilityKey);
                if (!actual || SCOPE_RANK[actual.scopeCeiling] < SCOPE_RANK[required.scopeCeiling]) {
                    errors.push({
                        code: 'PROTECTED_BASELINE_MISSING', path: `policy.entries.${position.positionId}.grants`,
                        message: `Approved baseline ${baseline.baselineId} requires ${required.capabilityKey}.`,
                        positionId: position.positionId, capabilityKey: required.capabilityKey,
                    });
                }
            }
        }
    }
    const roleGrants = rawPolicy.relationshipRoleGrants === undefined ? undefined : validateRelationshipRoleGrants(rawPolicy.relationshipRoleGrants, errors);
    const canonical = canonicalizePositionPolicy(roleGrants ? { entries, relationshipRoleGrants: roleGrants } : { entries });
    return errors.length === 0 ? { valid: true, errors: Object.freeze([]), value: canonical } : { valid: false, errors: Object.freeze(errors) };
}
export function getPolicyValidationErrors(policy, context) {
    return validatePositionPolicy(policy, context).errors;
}
export function getGrantsForPosition(policy, positionId) {
    return canonicalizePositionPolicy(policy).entries.find((entry) => entry.positionId === positionId)?.grants ?? [];
}
export function createDraftPolicyRevision(input, context) {
    const policyResult = validatePositionPolicy(input.policy, context);
    const errors = [...policyResult.errors];
    if (!isStablePositionId(input.revisionId) || !Number.isInteger(input.revisionNumber) || input.revisionNumber < 1 || !isIsoTimestamp(input.createdAt) || !input.createdByUid) {
        errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision', message: 'Draft revision metadata is malformed.' });
    }
    if (input.owner.kind === 'compassDefault' ? input.owner.ownerId !== 'compass' : !input.owner.ownerId) {
        errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision.owner', message: 'Policy owner is malformed.' });
    }
    if (errors.length || !policyResult.value)
        return { valid: false, errors: Object.freeze(errors) };
    return {
        valid: true,
        errors: Object.freeze([]),
        value: {
            revisionId: input.revisionId,
            revisionNumber: input.revisionNumber,
            schemaVersion: policySchemaVersionFor(policyResult.value),
            owner: input.owner,
            state: 'draft',
            createdAt: input.createdAt,
            createdByUid: input.createdByUid,
            previousRevisionId: input.previousRevisionId,
            reason: input.reason ?? null,
            policy: policyResult.value,
            fingerprint: null,
            publishedAt: null,
            publishedByUid: null,
        },
    };
}
function deepFreeze(value) {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        for (const child of Object.values(value))
            deepFreeze(child);
    }
    return value;
}
export function publishPolicyRevision(draft, publication, context) {
    if (draft.state !== 'draft') {
        return { valid: false, errors: [{ code: 'INVALID_REVISION_STATE', path: 'revision.state', message: 'Only a draft can be published.' }] };
    }
    const policyResult = validatePositionPolicy(draft.policy, context);
    if (!policyResult.valid || !policyResult.value)
        return { valid: false, errors: policyResult.errors };
    if (!isIsoTimestamp(publication.publishedAt) || !publication.publishedByUid) {
        return { valid: false, errors: [{ code: 'INVALID_REVISION_METADATA', path: 'revision.publication', message: 'Publication actor and timestamp are required.' }] };
    }
    return {
        valid: true,
        errors: Object.freeze([]),
        value: deepFreeze({
            ...draft,
            schemaVersion: policySchemaVersionFor(policyResult.value),
            state: 'published',
            policy: policyResult.value,
            fingerprint: fingerprintPositionPolicy(policyResult.value),
            publishedAt: publication.publishedAt,
            publishedByUid: publication.publishedByUid,
        }),
    };
}
/**
 * Compass defaults seed an explicit council draft; they are never a hidden
 * runtime overlay. Council customization edits the resulting complete
 * snapshot, subject to registry boundaries and approved baselines.
 */
export function createCouncilDraftFromDefault(defaultRevision, input, context) {
    const validated = validatePolicyRevision(defaultRevision, context);
    if (!validated.valid)
        return { valid: false, errors: validated.errors };
    if (defaultRevision.state !== 'published' || defaultRevision.owner.kind !== 'compassDefault') {
        return {
            valid: false,
            errors: [{
                    code: 'INVALID_REVISION_STATE',
                    path: 'defaultRevision',
                    message: 'Council drafts may only be seeded from a published Compass-default revision.',
                }],
        };
    }
    return createDraftPolicyRevision({
        revisionId: input.revisionId,
        revisionNumber: input.revisionNumber,
        owner: { kind: 'council', ownerId: input.tenantId },
        createdAt: input.createdAt,
        createdByUid: input.createdByUid,
        previousRevisionId: input.previousRevisionId,
        reason: input.reason,
        policy: defaultRevision.policy,
    }, context);
}
export function validatePolicyRevision(rawRevision, context) {
    const errors = [];
    if (!isRecord(rawRevision)) {
        return { valid: false, errors: [{ code: 'INVALID_POLICY_SHAPE', path: 'revision', message: 'Revision must be an object.' }] };
    }
    unexpectedFields(rawRevision, REVISION_FIELDS, 'revision', errors);
    if (rawRevision.schemaVersion !== POSITION_POLICY_SCHEMA_VERSION && rawRevision.schemaVersion !== POSITION_POLICY_ROLE_GRANTS_SCHEMA_VERSION) {
        errors.push({ code: 'INVALID_SCHEMA_VERSION', path: 'revision.schemaVersion', message: 'Unsupported Position policy schema version.' });
    }
    if (!POSITION_POLICY_STATES.includes(rawRevision.state)) {
        errors.push({ code: 'INVALID_REVISION_STATE', path: 'revision.state', message: 'Revision state must be draft or published.' });
    }
    if (!isStablePositionId(rawRevision.revisionId) || !Number.isInteger(rawRevision.revisionNumber) || rawRevision.revisionNumber < 1 || !isIsoTimestamp(rawRevision.createdAt) || typeof rawRevision.createdByUid !== 'string' || !rawRevision.createdByUid) {
        errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision', message: 'Revision identity, number, actor, or timestamp is malformed.' });
    }
    if (!isRecord(rawRevision.owner)) {
        errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision.owner', message: 'Revision owner is required.' });
    }
    else {
        unexpectedFields(rawRevision.owner, OWNER_FIELDS, 'revision.owner', errors);
        if ((rawRevision.owner.kind !== 'compassDefault' && rawRevision.owner.kind !== 'council') ||
            typeof rawRevision.owner.ownerId !== 'string' || !rawRevision.owner.ownerId ||
            (rawRevision.owner.kind === 'compassDefault' && rawRevision.owner.ownerId !== 'compass')) {
            errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision.owner', message: 'Revision owner is malformed.' });
        }
    }
    if (rawRevision.previousRevisionId !== null && !isStablePositionId(rawRevision.previousRevisionId)) {
        errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision.previousRevisionId', message: 'Previous revision ID is malformed.' });
    }
    if (rawRevision.reason !== null && typeof rawRevision.reason !== 'string') {
        errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision.reason', message: 'Revision reason must be text or null.' });
    }
    const policyResult = validatePositionPolicy(rawRevision.policy, context);
    errors.push(...policyResult.errors);
    if (policyResult.value
        && (rawRevision.schemaVersion === POSITION_POLICY_SCHEMA_VERSION || rawRevision.schemaVersion === POSITION_POLICY_ROLE_GRANTS_SCHEMA_VERSION)
        && rawRevision.schemaVersion !== policySchemaVersionFor(policyResult.value)) {
        errors.push({ code: 'INVALID_SCHEMA_VERSION', path: 'revision.schemaVersion', message: 'The schema version does not match the policy content (role grants require version 2; their absence requires version 1).' });
    }
    if (rawRevision.state === 'published') {
        if (!isIsoTimestamp(rawRevision.publishedAt) || typeof rawRevision.publishedByUid !== 'string' || !rawRevision.publishedByUid) {
            errors.push({ code: 'INVALID_REVISION_METADATA', path: 'revision.publication', message: 'Published revisions require actor and timestamp.' });
        }
        if (policyResult.value && rawRevision.fingerprint !== fingerprintPositionPolicy(policyResult.value)) {
            errors.push({ code: 'INVALID_FINGERPRINT', path: 'revision.fingerprint', message: 'Published fingerprint does not match canonical policy.' });
        }
    }
    else if (rawRevision.fingerprint !== null || rawRevision.publishedAt !== null || rawRevision.publishedByUid !== null) {
        errors.push({ code: 'INVALID_REVISION_STATE', path: 'revision', message: 'Drafts cannot carry publication metadata.' });
    }
    return errors.length === 0
        ? { valid: true, errors: Object.freeze([]), value: rawRevision }
        : { valid: false, errors: Object.freeze(errors) };
}
export function toCompilationEntries(revision, context) {
    const validated = validatePolicyRevision(revision, context);
    if (!validated.valid)
        return { valid: false, errors: validated.errors };
    if (revision.state !== 'published') {
        return { valid: false, errors: [{ code: 'INVALID_REVISION_STATE', path: 'revision.state', message: 'Only published policy is compilation-ready.' }] };
    }
    const value = Object.freeze(canonicalizePositionPolicy(revision.policy).entries.flatMap((entry) => entry.grants.map((grant) => Object.freeze({
        policyRevisionId: revision.revisionId,
        policyRevisionNumber: revision.revisionNumber,
        policyOwnerId: revision.owner.ownerId,
        positionId: entry.positionId,
        capabilityKey: grant.capabilityKey,
        scopeCeiling: grant.scopeCeiling,
    }))));
    return { valid: true, errors: Object.freeze([]), value };
}
/** Role grants of a PUBLISHED, valid revision, ready for the compiler. A revision without the section yields none. */
export function toRelationshipRoleGrants(revision, context) {
    const validated = validatePolicyRevision(revision, context);
    if (!validated.valid)
        return { valid: false, errors: validated.errors };
    if (revision.state !== 'published') {
        return { valid: false, errors: [{ code: 'INVALID_REVISION_STATE', path: 'revision.state', message: 'Only published policy is compilation-ready.' }] };
    }
    const value = Object.freeze((canonicalizePositionPolicy(revision.policy).relationshipRoleGrants ?? []).flatMap((entry) => entry.grants.map((grant) => Object.freeze({
        policyRevisionId: revision.revisionId,
        policyRevisionNumber: revision.revisionNumber,
        policyOwnerId: revision.owner.ownerId,
        role: entry.role,
        capabilityKey: grant.capabilityKey,
    }))));
    return { valid: true, errors: Object.freeze([]), value };
}
function safePathSegment(value, label) {
    if (!value || value.includes('/'))
        throw new TypeError(`${label} must be a non-empty Firestore path segment.`);
    return value;
}
export function councilPolicySetPath(tenantId) {
    return `tenants/${safePathSegment(tenantId, 'tenantId')}/positionPolicySets/operational`;
}
export function councilPolicyRevisionPath(tenantId, revisionId) {
    return `${councilPolicySetPath(tenantId)}/revisions/${safePathSegment(revisionId, 'revisionId')}`;
}
export function compassDefaultPolicyRevisionPath(revisionId) {
    return `positionPolicyDefaults/operational/revisions/${safePathSegment(revisionId, 'revisionId')}`;
}
export * from './migration.js';
export * from './guard.js';
